import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Btn, Container } from "@/components/site/Ui";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Solutions from "@/pages/Solutions";
import Advertisers from "@/pages/Advertisers";
import Publishers from "@/pages/Publishers";
import Verticals from "@/pages/Verticals";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Admin, { AuthCallback } from "@/pages/Admin";

const useSmoothScroll = () => {
  const lenis = useRef(null);
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const l = new Lenis({ duration: 1.1, anchors: { offset: -88 } });
    lenis.current = l;
    let id;
    const raf = (t) => { l.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); l.destroy(); lenis.current = null; };
  }, []);
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (!el) return;
        if (lenis.current) lenis.current.scrollTo(el, { offset: -88 });
        else el.scrollIntoView();
      }, 120);
      return () => clearTimeout(t);
    }
    if (lenis.current) lenis.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
};

const NotFound = () => (
  <Container className="py-32 text-center">
    <p className="eyebrow justify-center">404</p>
    <h1 className="mt-4 font-display text-4xl font-bold text-[#102B50]">Page not found</h1>
    <p className="mt-3 text-[#4A5D78]">The page you are looking for doesn’t exist.</p>
    <div className="mt-8 flex justify-center"><Btn to="/">Back to Home</Btn></div>
    <Link to="/contact" className="mt-4 inline-block text-sm text-[#2878E5]">Contact us</Link>
  </Container>
);

const Site = () => {
  useSmoothScroll();
  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-white focus:px-4 focus:py-2">Skip to content</a>
      <Header />
      <main id="main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/advertisers" element={<Advertisers />} />
          <Route path="/publishers" element={<Publishers />} />
          <Route path="/verticals" element={<Verticals />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<Legal doc="privacy" />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/cookie-policy" element={<Legal doc="cookies" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.pathname.startsWith("/admin")) return <Admin />;
  return <Site />;
};

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
